import React, { useState } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader, Disclaimer } from "../components/AppLayout";
import { Pill } from "../lib/ui";
import { GitCompareArrows, Play, Check, X, Bell, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MARKET_OPTS = [
  { code: "usa", label: "USA" }, { code: "eu", label: "EU" }, { code: "japan", label: "Japan" },
  { code: "china", label: "China" }, { code: "korea", label: "Korea" }, { code: "taiwan", label: "Taiwan" },
  { code: "turkey", label: "Türkiye" }, { code: "eaeu", label: "EAEU" },
];
const COMPLEXITY = { Low: "#16A34A", Moderate: "#D97706", High: "#EA580C", "Very High": "#DC2626" };
const EXAMPLES = [
  "Sostituzione dell'autoclave utilizzata per la sterilizzazione dei componenti della linea X.",
  "Installazione di una nuova linea isolatore per 7 prodotti sterili destinati a EU, USA, JP e CN.",
  "Nuovo fornitore di stopper per un prodotto liofilizzato registrato in EU e USA.",
];

export default function ChangeImpact() {
  const [desc, setDesc] = useState("");
  const [markets, setMarkets] = useState(["usa", "eu", "japan", "china"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [notif, setNotif] = useState(null);

  const run = async () => {
    if (!desc.trim()) return;
    setBusy(true); setResult(null); setNotif(null);
    try {
      const { data } = await api.post("/change-impact", { description: desc, markets });
      setResult(data);
      const n = await api.post("/client-notification", { description: desc, markets });
      setNotif(n.data);
    } catch (e) { toast.error(friendlyError(e)); } finally { setBusy(false); }
  };

  const toggle = (c) => setMarkets((m) => m.includes(c) ? m.filter((x) => x !== c) : [...m, c]);
  const A = result?.analysis || {};

  return (
    <div className="ri-fade-in max-w-5xl">
      <PageHeader overline="Change Impact Engine · Scenario Simulator" title="Change Impact"
        subtitle="Descrivi una modifica manufacturing/CMC: la piattaforma usa profilo sito, prodotti, clienti e knowledge base per un assessment multi-mercato." />

      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-5">
        <textarea data-testid="change-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
          placeholder="Es: Vogliamo sostituire l'autoclave utilizzata per la sterilizzazione dei componenti della linea X."
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {EXAMPLES.map((e, i) => (
            <button key={i} onClick={() => setDesc(e)} className="text-xs px-2.5 py-1 rounded-md border border-slate-200 text-slate-500 hover:border-blue-400 hover:bg-blue-50">{e.slice(0, 42)}...</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <div className="flex flex-wrap gap-1.5" data-testid="change-markets">
            {MARKET_OPTS.map((m) => (
              <button key={m.code} onClick={() => toggle(m.code)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border ${markets.includes(m.code) ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600"}`}>{m.label}</button>
            ))}
          </div>
          <button onClick={run} disabled={busy} data-testid="change-run-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-60">
            {busy ? "Analisi in corso..." : <><Play size={15} /> Genera assessment</>}
          </button>
        </div>
      </div>

      {busy && <div className="text-center py-10 text-slate-400"><div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />Valutazione multi-mercato in corso...</div>}

      {result && (
        <div className="space-y-5">
          <div className="rounded-xl bg-white border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-head font-semibold">Sintesi</h3>
              <span className="font-semibold text-sm" style={{ color: COMPLEXITY[result.complexity] }}>
                Complessità: {result.complexity}
              </span>
            </div>
            <p className="text-sm text-slate-600">{result.summary}</p>
            {result.complexity_reason && <p className="text-xs text-slate-500 mt-2 italic">{result.complexity_reason}</p>}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {[["GMP impact", A.gmp_impact], ["Quality impact", A.quality_impact], ["Validation impact", A.validation_impact],
              ["Regulatory impact", A.regulatory_impact], ["CTD impact", A.ctd_impact], ["Established Conditions", A.established_conditions],
              ["PACMP applicability", A.pacmp_applicability], ["Client impact", A.client_impact],
              ["Inspection impact", A.inspection_impact], ["Documentazione richiesta", A.documentation_required]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="ri-overline mb-1">{k}</div>
                <p className="text-sm text-slate-600">{v}</p>
              </div>
            ))}
          </div>

          {result.markets?.length > 0 && (
            <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
              <div className="px-5 py-3 border-b border-slate-100"><h3 className="font-head font-semibold text-sm">Matrice multi-mercato</h3></div>
              <table className="ri-table">
                <thead><tr><th>Mercato</th><th>Categoria</th><th>Prior approval</th><th>Notifica</th><th>Documenti</th><th>Tempistica</th><th>Fonte</th></tr></thead>
                <tbody>
                  {result.markets.map((m, i) => (
                    <tr key={i} data-testid={`change-market-row-${i}`}>
                      <td className="font-medium uppercase">{m.market}</td>
                      <td>{m.category}</td>
                      <td>{m.prior_approval ? <Check size={15} className="text-red-600" /> : <X size={13} className="text-slate-300" />}</td>
                      <td>{m.notification ? <Check size={15} className="text-green-600" /> : <X size={13} className="text-slate-300" />}</td>
                      <td className="text-xs text-slate-500">{m.documents}</td>
                      <td className="text-xs">{m.timing}</td>
                      <td className="text-xs text-slate-400">{m.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.potential_commitments?.length > 0 && (
            <div className="rounded-xl bg-white border border-slate-200 p-5">
              <h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-2"><AlertCircle size={15} className="text-orange-600" /> Commitments potenzialmente interessati</h3>
              <div className="flex flex-wrap gap-2">
                {result.potential_commitments.map((c, i) => <Pill key={i} color="#9A3412" bg="#FFEDD5">{c.product} — {c.type}</Pill>)}
              </div>
            </div>
          )}

          {notif?.rows?.length > 0 && (
            <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
              <div className="px-5 py-3 border-b border-slate-100"><h3 className="font-head font-semibold text-sm flex items-center gap-2"><Bell size={15} /> Notifiche clienti richieste</h3></div>
              <table className="ri-table">
                <thead><tr><th>Cliente</th><th>Notifica</th><th>Preavviso</th><th>Prior approval</th><th>Clausola</th></tr></thead>
                <tbody>
                  {notif.rows.map((r, i) => (
                    <tr key={i}><td className="font-medium">{r.client}</td><td>{r.notification}</td>
                      <td>{r.notice_days ? `${r.notice_days}gg` : "—"}</td><td>{r.prior_approval}</td>
                      <td className="text-xs text-slate-500">{r.clause}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.sources?.length > 0 && (
            <div className="text-xs text-slate-500">
              <span className="ri-overline">Fonti</span>
              <div className="mt-1 space-y-1">
                {result.sources.map((s) => <div key={s.n}>[{s.n}] {s.authority} — {s.title} {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">↗</a>}</div>)}
              </div>
            </div>
          )}
          <Disclaimer text={result.disclaimer} />
        </div>
      )}
    </div>
  );
}
